import React, { useState, useEffect } from 'react';
import * as moment from 'moment';
import * as csv from "csvtojson";
import { CartesianGrid, Legend, LineChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import * as randomColor from 'randomcolor';
import {Button, Grid} from '@material-ui/core';

import './App.css';

function App() {
  const dataConfirmed = 'https://github.com/CSSEGISandData/COVID-19/blob/master/csse_covid_19_data/csse_covid_19_time_series/time_series_covid19_confirmed_global.csv';
  const dataDeaths = 'https://raw.githubusercontent.com/CSSEGISandData/COVID-19/master/csse_covid_19_data/csse_covid_19_time_series/time_series_19-covid-Deaths.csv';
  const dataSourcePrefix = 'https://raw.githubusercontent.com/CSSEGISandData/COVID-19/master/csse_covid_19_data/csse_covid_19_daily_reports/';
  const dataSourceSuffix = '.csv';
  // const colors = randomColor({
  //   count: 56,
  //   seed: "hello",
  // });
  const colors = [
    '#ff7043', '#f10096', '#0072f0', '#03a9f4', '#00b6cb', '#737373', '#f15a60', '#7ac36a', '#ffa800', '#5a9bd4', '#f66d00', '#ec407a', '#faa75a', '#9e67ab', '#ce7058', '#f48fb1', '#81d4fa', '#d77fb3', '#5a9bd4', '#5e35b1'
  ]

  const [data, setData] = useState({});
  const [hideLines, setHideLines] = useState({us: [], world: []});

  const fetchRawData = async (url) => {
    const res = await fetch(url);
    const data = res.text();
    return data;
  };

  const transformToDate = async (data, usOnly) => {
    const result = [];
    let date = moment('3/10/2020');
    while (date < moment()) {
      date = date.add(1, 'days');
      const dateString = date.format('M/D/YY');
      const dateData = {
        name: dateString,
      };
      for (const row of data) {
        const count = Number.parseInt(row[dateString], 10);
        if (!Number.isNaN(count)) {
          const place = row[usOnly ? 'Province/State' : 'Country/Region'];
          dateData[place] = (dateData[place] ? dateData[place] : 0) + Number.parseInt(row[dateString], 10);
        }
      }
      if (Object.keys(dateData).length < 10) {
        break;
      }
      result.push(dateData);
    }
    return result;
  }

  useEffect(() => {
    (async () => {
      const types = ['Confirmed', 'Deaths'];
      const data = {
        us: types.reduce((acc, cur) => ({ ...acc, [cur]: {chartData: [], top20: null }}), {}),
        usDelta: types.reduce((acc, cur) => ({ ...acc, [cur]: {chartData: [], top20: null }}), {}),
        world: types.reduce((acc, cur) => ({ ...acc, [cur]: {chartData: [], top20: null }}), {}),
        worldDelta: types.reduce((acc, cur) => ({ ...acc, [cur]: {chartData: [], top20: null }}), {}),
      };
      let date = moment().subtract(14, 'days');
      const endDate = moment();
      let yesterdayData = null;
      while (date < endDate) {
        const dataCsv = await fetchRawData(`${dataSourcePrefix}${date.format('MM-DD-YYYY')}${dataSourceSuffix}`);
        const dataJson = await csv({
          ignoreColumns: /(^FIPS$|^Last_Update$|^Lat|^Long)/,
        }).fromString(dataCsv);
        if (dataJson.length === 0) {
          break;
        }
        const dateData = { us: {}, usDelta: {}, world: {}, worldDelta: {} };
        for (const type of types) {
          dateData.us[type] = { name: date.format('M-D-YY') };
          dateData.world[type] = { name: date.format('M-D-YY') };
          data.us[type].chartData.push(dateData.us[type]);
          data.world[type].chartData.push(dateData.world[type]);
          dateData.usDelta[type] = { name: date.format('M-D-YY') };
          dateData.worldDelta[type] = { name: date.format('M-D-YY') };
          data.usDelta[type].chartData.push(dateData.usDelta[type]);
          data.worldDelta[type].chartData.push(dateData.worldDelta[type]);
        }
        dataJson.forEach((row) => {
          // US Only
          if (row['Country_Region'] === 'US' || row['Country/Region'] === 'US') {
            const place = row['Province_State'] || row['Province/State'];
            for (const type of types) {
              dateData.us[type][place] = (dateData.us[type][place] ? dateData.us[type][place] : 0) + Number.parseInt(row[type], 10);
              if (yesterdayData !== null) {
                dateData.usDelta[type][place] = ((dateData.usDelta[type][place] !== undefined) ? dateData.usDelta[type][place] : (yesterdayData?.us[type][place] * -1 || 0)) + Number.parseInt(row[type], 10);
              }
            }
          }
          // World
          const place = row['Country_Region'] || row['Country/Region'];
          for (const type of types) {
            dateData.world[type][place] = (dateData.world[type][place] ? dateData.world[type][place] : 0) + Number.parseInt(row[type], 10);
            if (yesterdayData !== null) {
              dateData.worldDelta[type][place] = ((dateData.worldDelta[type][place] !== undefined) ? dateData.worldDelta[type][place] : (yesterdayData?.world[type][place] * -1 || 0)) + Number.parseInt(row[type], 10);
            }
          }
        });
        date = date.add(1, 'days');
        yesterdayData = dateData;
      }
      for (let type of types) {
        data.us[type].top20 = Object.entries(data.us[type].chartData[data.us[type].chartData.length - 1])
          .filter(([k, v]) => k !== 'name')
          .sort(([ak, av], [bk, bv]) => bv - av)
          .map(([k, v]) => k)
          .slice(0, 20);;
        data.world[type].top20 = Object.entries(data.world[type].chartData[data.world[type].chartData.length - 1])
          .filter(([k, v]) => k !== 'name')
          .sort(([ak, av], [bk, bv]) => bv - av)
          .map(([k, v]) => k)
          .slice(0, 20);;
      }
      setData(data);

      // Old
      // const dataConfirmedCsv = await fetchRawData(dataConfirmed);
      // const dataConfirmedJson = await csv({
      //   ignoreColumns: /(^Lat$|^Long$|^1\/\d\d?\/20$|^2\/\d\d?\/20$)/,
      // }).fromString(dataConfirmedCsv);
      // const dataConfirmedUSJson = dataConfirmedJson.filter((row) => (
      //   row['Country/Region'] === 'US' && row['Province/State'].indexOf(',') === -1) && !['Diamond Princess', 'Grand Princess'].includes(row['Province/state'])
      // );
      // const dataConfirmedUSChartFormat = await transformToDate(dataConfirmedUSJson, true);
      // const dataConfirmedWorldChartFormat = await transformToDate(dataConfirmedJson, false);
      // const top20ConfirmedUS = Object.entries(dataConfirmedUSChartFormat[dataConfirmedUSChartFormat.length - 1])
      //   .filter(([k, v]) => k !== 'name')
      //   .sort(([ak, av], [bk, bv]) => bv - av)
      //   .map(([k, v]) => k)
      //   .slice(0, 20);
      // const top20ConfirmedWorld = Object.entries(dataConfirmedWorldChartFormat[dataConfirmedWorldChartFormat.length - 1])
      //   .filter(([k, v]) => k !== 'name')
      //   .sort(([ak, av], [bk, bv]) => bv - av)
      //   .map(([k, v]) => k)
      //   .slice(0, 20);
      // setTop20ConfirmedUS(top20ConfirmedUS);
      // setDataConfirmedUS(dataConfirmedUSChartFormat);
      // setTop20ConfirmedWorld(top20ConfirmedWorld);
      // setDataConfirmedWorld(dataConfirmedWorldChartFormat);

      // const dataDeathsCsv = await fetchRawData(dataDeaths);
      // const dataDeathsJson = await csv({
      //   ignoreColumns: /(^Lat$|^Long$|^1\/\d\d?\/20$|^2\/\d\d?\/20$)/,
      // }).fromString(dataDeathsCsv);
      // const dataDeathsUS = dataDeathsJson.filter((row) => (
      //   row['Country/Region'] === 'US' && row['Province/State'].indexOf(',') === -1) && !['Diamond Princess', 'Grand Princess'].includes(row['Province/state'])
      // );
      // const dataDeathsUSChartFormat = await transformToDate(dataDeathsUS, true);
      // const dataDeathsWorldChartFormat = await transformToDate(dataDeathsJson, false);
      // const top20DeathsUS = Object.entries(dataDeathsUSChartFormat[dataDeathsUSChartFormat.length - 1])
      //   .filter(([k, v]) => k !== 'name')
      //   .sort(([ak, av], [bk, bv]) => bv - av)
      //   .map(([k, v]) => k)
      //   .slice(0, 20);
      // const top20DeathsWorld = Object.entries(dataDeathsWorldChartFormat[dataDeathsWorldChartFormat.length - 1])
      //   .filter(([k, v]) => k !== 'name')
      //   .sort(([ak, av], [bk, bv]) => bv - av)
      //   .map(([k, v]) => k)
      //   .slice(0, 20);
      // setTop20DeathsUS(top20DeathsUS);
      // setDataDeathsUS(dataDeathsUSChartFormat);
      // setTop20DeathsWorld(top20DeathsWorld);
      // setDataDeathsWorld(dataDeathsWorldChartFormat);
    })();
  }, []);

  return (
    ['us', 'world'].map(region => (
      <Grid container justify="center" alignItems="center">
        <Grid item xs={12} lg={12} xl={12} style={{ textAlign: 'center' }}>
          <h2>{region}</h2>
        </Grid>
        <Grid item xs={12} lg={6} style={{ textAlign: 'center' }}>
          <h3>Confirmed Cases</h3>
          <LineChart width={640} height={640} data={data[region]?.Confirmed?.chartData} style={{ margin: 'auto' }}>
            {data[region]?.Confirmed?.top20.map((k, i) => (
                <Line type="monotone" dataKey={`${k}${hideLines[region].includes(k) ? ' ' : ''}`} strokeWidth="2" stroke={colors[i]} activeDot={{ r: 6 }} dot={false} />
            ))}
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip labelStyle={{ fontWeight: 'bold' }} itemStyle={{ fontWeight: 'bold' }} allowEscapeViewBox={{ x: true, y: false}} position={{ x: 640, y: 0 }} />
            <Legend
              iconType="plainline"
              onClick={(e) => {
                const key = e.dataKey.trim();
                const index = hideLines[region].indexOf(key);
                if (hideLines[region].includes(key)) {
                  const newHideLines = [...hideLines[region]];
                  newHideLines.splice(index, 1);
                  setHideLines({...hideLines, [region]: newHideLines });
                } else {
                  setHideLines({...hideLines, [region]: hideLines[region].concat(key)});
                }
              }}
              formatter={(v, e, i) => (<span style={{ color: v.endsWith(' ') ? 'silver' :  e.color, cursor: 'pointer'}}>{v}</span>)}
            />
          </LineChart>
          <Button size="small" onClick={() => setHideLines({...hideLines, [region]: [] })} style={{ textTransform: 'lowercase' }}>
            select all
          </Button>
          |
          <Button size="small" onClick={() => setHideLines({...hideLines, [region]: [...new Set([...data[region]?.Confirmed?.top20, ...data[region]?.Deaths?.top20])] })} style={{ textTransform: 'lowercase' }}>
          clear all
          </Button>
        </Grid>
        <Grid item xs={12} lg={6} style={{ textAlign: 'center' }}>
          <h3>Deaths</h3>
          <LineChart width={640} height={640} data={data[region]?.Deaths?.chartData} style={{ margin: 'auto' }}>
            {data[region]?.Deaths?.top20.map((k, i) => (
                <Line type="monotone" dataKey={`${k}${hideLines[region].includes(k) ? ' ' : ''}`} strokeWidth="2" stroke={colors[i]} activeDot={{ r: 6 }} dot={false} />
            ))}
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip labelStyle={{ fontWeight: 'bold' }} itemStyle={{ fontWeight: 'bold' }} allowEscapeViewBox={{ x: true, y: false}} position={{ x: 640, y: 0 }} />
            <Legend
              iconType="plainline"
              onClick={(e) => {
                const key = e.dataKey.trim();
                const index = hideLines[region].indexOf(key);
                if (hideLines[region].includes(key)) {
                  const newHideLines = [...hideLines[region]];
                  newHideLines.splice(index, 1);
                  setHideLines({...hideLines, [region]: newHideLines });
                } else {
                  setHideLines({...hideLines, [region]: hideLines[region].concat(key)});
                }
              }}
              formatter={(v, e, i) => (<span style={{ color: v.endsWith(' ') ? 'silver' :  e.color, cursor: 'pointer'}}>{v}</span>)}
            />
          </LineChart>
          <Button size="small" onClick={() => setHideLines({...hideLines, [region]: [] })} style={{ textTransform: 'lowercase' }}>
            select all
          </Button>
          |
          <Button size="small" onClick={() => setHideLines({...hideLines, [region]: [...new Set([...data[region]?.Confirmed?.top20, ...data[region]?.Deaths?.top20])] })} style={{ textTransform: 'lowercase' }}>
          clear all
          </Button>
        </Grid>
        <Grid item xs={12} lg={6} style={{ textAlign: 'center' }}>
          <h3>Confirmed Cases Delta</h3>
          <LineChart width={640} height={640} data={data[`${region}Delta`]?.Confirmed?.chartData} style={{ margin: 'auto' }}>
            {data[region]?.Confirmed?.top20.map((k, i) => (
                <Line type="monotone" dataKey={`${k}${hideLines[region].includes(k) ? ' ' : ''}`} strokeWidth="2" stroke={colors[i]} activeDot={{ r: 6 }} dot={false} />
            ))}
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip labelStyle={{ fontWeight: 'bold' }} itemStyle={{ fontWeight: 'bold' }} allowEscapeViewBox={{ x: true, y: false}} position={{ x: 640, y: 0 }} />
            <Legend
              iconType="plainline"
              onClick={(e) => {
                const key = e.dataKey.trim();
                const index = hideLines[region].indexOf(key);
                if (hideLines[region].includes(key)) {
                  const newHideLines = [...hideLines[region]];
                  newHideLines.splice(index, 1);
                  setHideLines({...hideLines, [region]: newHideLines });
                } else {
                  setHideLines({...hideLines, [region]: hideLines[region].concat(key)});
                }
              }}
              formatter={(v, e, i) => (<span style={{ color: v.endsWith(' ') ? 'silver' :  e.color, cursor: 'pointer'}}>{v}</span>)}
            />
          </LineChart>
          <Button size="small" onClick={() => setHideLines({...hideLines, [region]: [] })} style={{ textTransform: 'lowercase' }}>
            select all
          </Button>
          |
          <Button size="small" onClick={() => setHideLines({...hideLines, [region]: [...new Set([...data[region]?.Confirmed?.top20, ...data[region]?.Deaths?.top20])] })} style={{ textTransform: 'lowercase' }}>
          clear all
          </Button>
        </Grid>
        <Grid item xs={12} lg={6} style={{ textAlign: 'center' }}>
          <h3>Deaths Delta</h3>
          <LineChart width={640} height={640} data={data[`${region}Delta`]?.Deaths?.chartData} style={{ margin: 'auto' }}>
            {data[region]?.Deaths?.top20.map((k, i) => (
                <Line type="monotone" dataKey={`${k}${hideLines[region].includes(k) ? ' ' : ''}`} strokeWidth="2" stroke={colors[i]} activeDot={{ r: 6 }} dot={false} />
            ))}
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip labelStyle={{ fontWeight: 'bold' }} itemStyle={{ fontWeight: 'bold' }} allowEscapeViewBox={{ x: true, y: false}} position={{ x: 640, y: 0 }} />
            <Legend
              iconType="plainline"
              onClick={(e) => {
                const key = e.dataKey.trim();
                const index = hideLines[region].indexOf(key);
                if (hideLines[region].includes(key)) {
                  const newHideLines = [...hideLines[region]];
                  newHideLines.splice(index, 1);
                  setHideLines({...hideLines, [region]: newHideLines });
                } else {
                  setHideLines({...hideLines, [region]: hideLines[region].concat(key)});
                }
              }}
              formatter={(v, e, i) => (<span style={{ color: v.endsWith(' ') ? 'silver' :  e.color, cursor: 'pointer'}}>{v}</span>)}
            />
          </LineChart>
          <Button size="small" onClick={() => setHideLines({...hideLines, [region]: [] })} style={{ textTransform: 'lowercase' }}>
            select all
          </Button>
          |
          <Button size="small" onClick={() => setHideLines({...hideLines, [region]: [...new Set([...data[region]?.Confirmed?.top20, ...data[region]?.Deaths?.top20])] })} style={{ textTransform: 'lowercase' }}>
          clear all
          </Button>
        </Grid>
      </Grid>
    ))
  );
}

export default App;
